import { useTranslation } from "react-i18next";
import { MapPin, Star } from "lucide-react";
import { Link } from "react-router-dom";
import type { MekkeSpotResponseDto } from "@tabipla/shared";
import { getCategoryById } from "../../../config/categories";
import { getCategoryGradient } from "../../../utils/style-config";

interface SpotCardProps {
  spot: MekkeSpotResponseDto;
}

export function SpotCard({ spot }: SpotCardProps) {
  const { t } = useTranslation();
  const category = getCategoryById(spot.category);
  const CategoryIcon = category.icon;

  return (
    <Link
      to={`/spots/${spot.id}`}
      className="group block overflow-hidden rounded-xl border bg-card shadow-sm transition-all hover:shadow-md active:scale-[0.99]"
    >
      {/* Image Section - Square aspect ratio for compact grid */}
      <div className="relative aspect-square overflow-hidden bg-muted">
        {spot.images.length > 0 ? (
          <img
            src={spot.images[0].url}
            alt={spot.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div
            className="relative h-full w-full"
            style={{
              background: getCategoryGradient(spot.category).gradient,
            }}
          >
            {/* Decorative circles */}
            <div
              className="absolute -right-4 -top-4 h-16 w-16 rounded-full"
              style={{
                backgroundColor: getCategoryGradient(spot.category).decor1,
              }}
            />
            <div
              className="absolute -bottom-2 -left-2 h-12 w-12 rounded-full"
              style={{
                backgroundColor: getCategoryGradient(spot.category).decor2,
              }}
            />
            {/* Category icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <CategoryIcon className="h-12 w-12 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
            </div>
          </div>
        )}

        {/* Status Badge - Top Left */}
        <span
          className={`absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm ${
            spot.status === "visited" ? "bg-indigo-500 text-white" : "bg-amber-500 text-white"
          }`}
        >
          {t(`spots.${spot.status === "visited" ? "visited" : "wantToGo"}`)}
        </span>

        {/* Image Count - Top Right */}
        {spot.images.length > 1 && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {spot.images.length}
          </span>
        )}

        {/* Rating - Bottom Right */}
        {spot.rating && (
          <span className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
            {spot.rating}
          </span>
        )}
      </div>

      {/* Content Section - Compact */}
      <div className="p-2">
        {/* Category */}
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-medium"
          style={{ color: category.color }}
        >
          <CategoryIcon className="h-2.5 w-2.5" />
          {t(category.labelKey)}
        </span>

        {/* Title */}
        <h3 className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight">{spot.name}</h3>

        {/* Address */}
        {spot.address && (
          <p className="mt-0.5 flex items-start gap-0.5 text-[10px] text-muted-foreground">
            <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" />
            <span className="line-clamp-1">{spot.address}</span>
          </p>
        )}
      </div>
    </Link>
  );
}
